import { useEffect, useMemo } from 'react';
import * as React from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import Prism from 'prismjs';
import styled, { css, ThemeProvider } from 'styled-components';

import { type BlogPost } from '../../templates/blogPostTemplate';
import { ANIMATIONS, STICKY } from '../../utils';
import { SOCIAL_SHARE } from '../../utils/social-share-images';
import { ColorVars, LIGHT_THEME } from '../../utils/theme/themes';
import { RunningText } from '../design-system';
import { HeadingFourStyles, HeadingTwoStyles } from '../design-system/Heading';
import { Page } from '../global/Page';
import { RichText } from '../global/RichText';
import { SocialShare } from '../global/SocialShare';
import { Recommendations } from '../use-cases/Recommendations';

import { BlogKicker, type ButtonOption } from './BlogKicker';
import { type AsideHeading, BlogPostAside } from './BlogPostAside';
import { BlogPostHeader } from './BlogPostHeader';
import * as FOD from './friends-of-data';
import { SEOStructuredData } from './SEOStructuredData';
import { flattenNodeText, textToId } from './utils';

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  max-width: none;
  padding: 0;
  width: 100%;
`;

const Content = styled.div<{
  $hasHeroMedia?: boolean;
  $isFriendsOfData?: boolean;
}>`
  ${({ theme }) => theme.card};
  padding: 40px;
  display: flex;
  align-items: start;
  justify-content: center;
  gap: 45px;

  ${({ $isFriendsOfData }) =>
    $isFriendsOfData &&
    css`
      box-shadow: none;
      border: 1px solid ${({ theme }) => theme.borderColor.MUTED};
      border-top: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
      border-bottom: none;
    `}

  ${({ $hasHeroMedia }) =>
    $hasHeroMedia &&
    css`
      padding-top: 250px;

      @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
        padding-top: 90px;
      }

      @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
        padding-top: 180px;
      }
    `}
`;

const ThisText = styled(RunningText)<{ $isFriendsOfData?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin: 0 auto;
  max-width: 740px;
  width: 100%;

  ${props =>
    props.$isFriendsOfData &&
    css`
      --social-text-transform: capitalize;
      --social-font-weight: 400;

      h2 {
        ${HeadingTwoStyles}
      }

      h1,
      h2,
      h3 {
        font-family: ${({ theme }) => theme.fontFamily.DISPLAY};
        letter-spacing: 0;
      }

      h3 {
        font-size: 20px;
        line-height: 1.4;
      }

      blockquote {
        margin: 0;
        padding: 0.4em 0 3em;
        border: 0;
        max-width: 456px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1em;

        &::before {
          content: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyNSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTcuMiAxMC4wMTEyQzguNjQgMTAuMDExMiA5LjY4IDEwLjU3MTIgMTAuNjQgMTEuODUxMkMxMS4xMiAxMi44MTEyIDExLjY4IDEyLjczMTIgMTEuOTIgMTEuNjkxMkMxMy4yOCA2LjgxMTIzIDE1Ljg0IDIuMTcxMjQgMjAuNTYgMC4xNzEyMzNDMjIuNCAtMC40Njg3NjQgMjMuNDQgMC44MTEyMzQgMjMuNDQgMS45MzEyM0MyMy40NCAyLjQ5MTI0IDIzLjIgMi44OTEyNCAyMi42NCAzLjIxMTI0QzE4LjQgNC45NzEyMyAxMy45MiA3LjY5MTI0IDEzLjkyIDEwLjgxMTJDMTMuOTIgMTEuNzcxMiAxNC4zMiAxMi40MTEyIDE0Ljg4IDEyLjQxMTJDMTUuNzYgMTIuNDExMiAxNi4yNCAxMC4wMTEyIDE5LjQ0IDEwLjAxMTJDMjIuNDggMTAuMDExMiAyNC4wOCAxMi4zMzEyIDI0LjA4IDE0LjczMTJDMjQuMDggMTcuNTMxMiAyMi4wOCAxOS43NzEyIDE4LjY0IDE5Ljc3MTJDMTYuNDggMTkuNzcxMiAxNC42NCAxOC42NTEyIDEyLjg4IDE2LjE3MTJDMTIuNCAxNS4yOTEyIDExLjc2IDE1LjI5MTIgMTEuMzYgMTYuMjUxMkMxMC4yNCAxOC41NzEyIDguNjQgMTkuNzcxMiA2LjQgMTkuNzcxMkMyLjg4IDE5Ljc3MTIgMCAxNi45NzEyIDAgMTIuNjUxMkMwIDguMDkxMjQgMi44OCAyLjQ5MTI0IDguMzIgMC4xNzEyMzNDMTAuMTYgLTAuNDY4NzY0IDExLjIgMC44OTEyMzMgMTEuMiAxLjkzMTIzQzExLjIgMi41NzEyNCAxMC45NiAyLjg5MTI0IDEwLjMyIDMuMjExMjRDNi4xNiA1LjA1MTI0IDEuMzYgNy42OTEyNCAxLjM2IDEwLjgxMTJDMS4zNiAxMS43NzEyIDEuNzYgMTIuNDExMiAyLjQgMTIuNDExMkMzLjI4IDEyLjQxMTIgNC4wOCAxMC4wMTEyIDcuMiAxMC4wMTEyWiIgZmlsbD0iIzVGNTA5RCIvPgo8L3N2Zz4K');
        }

        p {
          ${HeadingFourStyles}
          font-family: ${({ theme }) => theme.fontFamily.DISPLAY};
          letter-spacing: 0;
          text-align: center;
          line-height: 1.7;
        }
      }

      p {
        color: ${ColorVars.OBSIDIAN};
      }
    `}

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin: 0 !important;
  }
`;

const SeeMore = styled.div`
  width: 100%;
  padding: 80px 40px;
  background: ${({ theme }) => theme.gradientFade};
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 60px 20px;
  }
`;

interface BlogPostContentProps {
  post: BlogPost;
}

export const BlogPostContent: React.ComponentType<BlogPostContentProps> =
  React.memo(({ post }) => {
    const json = post.body?.raw ? JSON.parse(post.body.raw) : null;

    const categories = useMemo(() => {
      return post.categories ? post.categories.map(cat => cat.name) : [];
    }, [post.categories]);

    const blogKickerButtons: ButtonOption[] | undefined = useMemo(() => {
      const buttons = [];
      const kicker = post.blogKicker;

      if (kicker) {
        if (kicker.buttonUrl1 && kicker.buttonLabel1) {
          buttons.push({
            link: kicker.buttonUrl1,
            text: kicker.buttonLabel1
          });
        }

        if (kicker.buttonUrl2 && kicker.buttonLabel2) {
          buttons.push({
            link: kicker.buttonUrl2,
            text: kicker.buttonLabel2
          });
        }
      }

      return buttons.length > 0 ? buttons : undefined;
    }, [post.blogKicker]);

    const OGImage = post.socialSharePreviewImage
      ? post.socialSharePreviewImage.url
      : SOCIAL_SHARE.Blog;

    const headings: AsideHeading[] = json?.content
      ? (
          json.content as Array<{
            content: Array<{ nodeType: string; value: string }>;
            nodeType: string;
          }>
        )
          .filter(node => node.nodeType === 'heading-2')
          .map(flattenNodeText)
          .map(text => ({
            id: textToId(text),
            text: text
          }))
      : [];

    const shouldShowAsideNav = headings.length > 1 && !post.disableSideNav;

    const hasHeroMedia =
      (post.heroLoomVideo !== undefined || post.heroImage !== undefined) &&
      !post.interview;

    const isFriendsOfData = post.friendsOfData;

    useEffect(() => {
      Prism.highlightAll();
    }, []);

    return (
      <Page
        seoProps={{
          description: post.seoMetaDescription,
          image: OGImage,
          title: post.seoTitle,
          url: post.slug
        }}
        theme={isFriendsOfData ? FOD.theme : undefined}>
        <SEOStructuredData
          author={post.author}
          date={post.date}
          image={post.heroImage?.url}
          synopsis={post.synopsis || ''}
          title={post.title}
        />
        <Wrapper {...ANIMATIONS.SimpleFadeIn}>
          {isFriendsOfData ? (
            <>
              <FOD.Fonts />
              <FOD.Header post={post} />
            </>
          ) : (
            <BlogPostHeader categories={categories} post={post} />
          )}

          <ThemeProvider
            theme={
              isFriendsOfData
                ? { ...FOD.theme, backgroundColor: LIGHT_THEME.backgroundColor }
                : LIGHT_THEME
            }>
            <Content
              $hasHeroMedia={hasHeroMedia}
              $isFriendsOfData={isFriendsOfData}
              className={classNames({
                [STICKY.Boundary]: shouldShowAsideNav
              })}>
              {shouldShowAsideNav && <BlogPostAside headings={headings} />}
              <ThisText
                $isFriendsOfData={isFriendsOfData}
                style={{
                  marginRight: shouldShowAsideNav ? '20%' : 'auto',
                  marginLeft: shouldShowAsideNav ? '0' : 'auto'
                }}>
                <RichText body={post.body} />
                <SocialShare title={post.title} />
                {post.blogKicker ? (
                  <BlogKicker
                    buttons={blogKickerButtons}
                    text={post.blogKicker.body.body}
                  />
                ) : (
                  <BlogKicker />
                )}
              </ThisText>
            </Content>
          </ThemeProvider>
          {post.recommendations && (
            <SeeMore>
              <Recommendations
                category={categories}
                isblog={true}
                items={post.recommendations}
                showTron={false}
              />
            </SeeMore>
          )}
        </Wrapper>
      </Page>
    );
  });
